import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../logic/store/Store';
import {
	AiExperience,
	setSelectedTerritoryId,
	setSelectedSalesRepId,
	setSelectedSellerId,
	setSelectedGrowerId,
	clearSelectedTerritoryAnalysis,
	clearSelectedSalesRepAnalysis,
	clearSelectedSellerAnalysis,
	clearSelectedGrowerAnalysis,
	clearState as clearInsightsState,
} from '../../logic/store/Insights/InsightsSlice';
import { getAiExperience, getGrowerAnalysis, getSalesRepAnalyses, getSalesRepAnalysis, getSellerAnalysis, getSellerMemberAnalysis, getTerritoryAnalyses, getTerritoryAnalysis } from '../../logic/store/Insights/InsightThunks';
import { MultiTerritoryView } from './MultiTerritoryView';
import { SingleTerritoryView } from './SingleTerritoryView';
import { MultiSalesRepView } from './MultiSalesRepView';
import { SingleSalesRepView } from './SingleSalesRepView';
import { SellerView } from './SellerView';
import { GrowerView } from './GrowerView';
import { useHistory, useParams } from 'react-router-dom';
import { EventStructureTags } from '../../tracing/EventTagNames';
import { useScopedSession } from '../../tracing/session';
import { EventInsightsNames } from '../../tracing/EventNames';
import { useAmplitudePassthrough } from '../../logic/Utility/useAmplitude';
import { RootCauseLogo } from './Icons/RootCauseLogo';
import { SellerMembersView } from './SellerMembersView';

const MainContainer = styled.div`
	display: flex;
	height: 100%;
	padding: 1.5% 5%;
	margin: 0 auto;
	flex-direction: column;
`;

const InnerContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
`;

export enum InsightsViewState
{
	Default, // No Insights Available or loading experience
	MultiTerritory, // Multiple Territory Analyses View
	Territory, // Single Territory Analysis View
	MultiSalesRep, // Multiple Sales Rep Analyses View
	SalesRep, // Single Sales Rep Analysis View
	SellerMembers, // Seller members Insights View
	Seller, // Seller Insights View
	Grower, // Grower Insights View
}

interface IInsightsViewState
{
	view: InsightsViewState.Default
	| InsightsViewState.MultiTerritory
	| InsightsViewState.Territory
	| InsightsViewState.MultiSalesRep
	| InsightsViewState.SalesRep
	| InsightsViewState.SellerMembers
	| InsightsViewState.Seller
	| InsightsViewState.Grower
}

type IInsightsViewMode = IInsightsViewState;

type InsightsRouteParams =
{
	territoryId?: string;
	salesRepId?: string;
	sellerId?: string;
	growerId?: string;
};

interface IAiInsightsProps
{
	
}

/* AKA Root Cause */
export const AiInsights = (props: IAiInsightsProps) =>
{
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const history = useHistory();

	const session = useScopedSession(AiInsights.name, {}, {
		[EventStructureTags.PageContext]: 'rootcause',
		[EventStructureTags.PageUrl]: window.location.toString()
	});

	// Deep link params
	const { territoryId, salesRepId, sellerId, growerId } = useParams<InsightsRouteParams>();	

	const [currentViewState, setCurrentViewState] = useState<IInsightsViewMode>({view: InsightsViewState.Default});

	const currentLoggedInUserName = useAppSelector((state) => state.userInfo.UserName);
	const currentUserId = useAppSelector((state) => state.userInfo.UserId);

	// AI Experience objects from slice
	const aiExperience: AiExperience = useAppSelector(s => s.insights.aiExperience);
	const loadingExperience = useAppSelector(s => s.insights.isLoadingExperience);

	const hasDeepLink = !!territoryId || !!salesRepId || !!sellerId || !!growerId;

	// Territories the caller owns. Drives whether the manager experience is shown at all,
	// and whether there is a list to go back to from a single territory.
	const territoryAnalyses = useAppSelector(s => s.insights.territoryAnalyses);
	const currentTerritoryId = useAppSelector(s => s.insights.currentTerritoryId);

	useEffect(() =>
	{
		dispatch(getAiExperience());
	}, []);

	// Resolve the manager experience: territory when the caller owns one, sales rep list otherwise.
	const loadManagerExperience = async () =>
	{
		try
		{
			const territories = await dispatch(getTerritoryAnalyses()).unwrap();

			if (territories?.length === 1)
			{
				// Only one territory, so the list would be a pointless extra click
				const only = territories[0];
				dispatch(setSelectedTerritoryId(only.TerritoryExternalId));
				await dispatch(getTerritoryAnalysis({ territoryId: only.TerritoryExternalId })).unwrap();
				setCurrentViewState({ view: InsightsViewState.Territory });
				return;
			}

			if (territories?.length > 1)
			{
				setCurrentViewState({ view: InsightsViewState.MultiTerritory });
				return;
			}
		}
		catch (e)
		{
			// Falling back to the sales rep list is right when the user simply owns no territory,
			// but a backend failure lands here too -- surface it so it is not mistaken for the former.
			console.warn('Root Cause: territory lookup failed, falling back to the sales rep list.', e);
		}

		dispatch(getSalesRepAnalyses());
		setCurrentViewState({ view: InsightsViewState.MultiSalesRep });
	};

	useEffect(() =>
	{
		// Deep links handle their own data loading in a separate useEffect
		if (hasDeepLink)
		{
			return;
		}

		if (!loadingExperience && aiExperience)
		{
			// A manager reaches us as 'MultiSalesRep'. Whether they also own a territory node is
			// something only the territory endpoint can answer, so ask it before choosing the view
			// and fall back to the sales rep list when they own none.
			if (aiExperience === 'MultiSalesRep')
			{
				loadManagerExperience();
				return;
			}

			// Parent is responsible for initial data loading; child views only display data
			switch (aiExperience)
			{
				case 'SalesRep':
					dispatch(setSelectedSalesRepId(currentUserId));
					dispatch(getSalesRepAnalysis({ salesRepId: currentUserId }));
					break;
				case 'Seller':
					dispatch(getSellerMemberAnalysis());
					break;
				case 'Grower':
					dispatch(setSelectedGrowerId(currentUserId));
					dispatch(getGrowerAnalysis({ growerId: currentUserId }));
					break;
				default:
					console.error(`AI Experience of type ${aiExperience} is not supported.`);
					break;
			}

			setCurrentViewState({ view: mapExperienceToView(aiExperience) });
		}
		else
		{
			setCurrentViewState({ view: InsightsViewState.Default });
		}
	}, [hasDeepLink, loadingExperience, aiExperience, currentUserId, dispatch]);

	useEffect(() =>
	{
		if (!hasDeepLink)
		{
			return;
		}

		const load = async () =>
		{
			try
			{
				// Clear the insights state
				dispatch(clearInsightsState());

				// Deepest wins: grower > seller > salesrep
				if (growerId)
				{
					dispatch(setSelectedGrowerId(growerId));
					const result = await dispatch(getGrowerAnalysis({ growerId })).unwrap();
					setCurrentViewState({ view: InsightsViewState.Grower });
					return;
				}

				if (sellerId)
				{
					dispatch(setSelectedSellerId(sellerId));
					const result = await dispatch(getSellerAnalysis({ sellerId })).unwrap();
					setCurrentViewState({ view: InsightsViewState.Seller });
					return;
				}

				if (salesRepId)
				{
					dispatch(setSelectedSalesRepId(salesRepId));
					const result = await dispatch(getSalesRepAnalysis({ salesRepId })).unwrap();
					setCurrentViewState({ view: InsightsViewState.SalesRep });
					return;
				}

				if (territoryId)
				{
					dispatch(setSelectedTerritoryId(territoryId));
					await dispatch(getTerritoryAnalysis({ territoryId })).unwrap();
					setCurrentViewState({ view: InsightsViewState.Territory });
					return;
				}

				// If we somehow got here, fallback
				history.replace('/rootcause');
			}
			catch
			{
				// If any deep link fetch fails (no access / not found), go to base
				history.replace('/rootcause');
			}
		};

		load();
	}, [hasDeepLink, territoryId, salesRepId, sellerId, growerId, dispatch, history]);

	const mapExperienceToView = (experience: AiExperience): InsightsViewState =>
	{
		switch (experience)
		{
			case 'MultiSalesRep': 
				return InsightsViewState.MultiSalesRep;
			case 'SalesRep':      
				return InsightsViewState.SalesRep;
			case 'Seller':        
				return InsightsViewState.SellerMembers;
			case 'Grower':        
				return InsightsViewState.Grower;
			default:              
				return InsightsViewState.Default; // 'None' or 'Unknown'
		}
	};

	// Set the selected Territory and change the view to Single Territory view
	const selectTerritory = useAmplitudePassthrough(session, EventInsightsNames.OpenInsightsForTerritory, (Id: string, snapshotDate?: string) =>
	{
		dispatch(setSelectedTerritoryId(Id));
		dispatch(getTerritoryAnalysis({ territoryId: Id, snapshotDate }));
		setCurrentViewState({ view: InsightsViewState.Territory });
	}, [dispatch, setSelectedTerritoryId, getTerritoryAnalysis, setCurrentViewState]);

	// Set the selected Sales Rep and change the view to Single Sales Rep view
	const selectSalesRep = useAmplitudePassthrough(session, EventInsightsNames.OpenInsightsForSalesRep, (Id: string, snapshotDate?: string) =>
	{
		dispatch(setSelectedSalesRepId(Id));
		dispatch(getSalesRepAnalysis({ salesRepId: Id, snapshotDate }));
		setCurrentViewState({ view: InsightsViewState.SalesRep });
	}, [dispatch, setSelectedSalesRepId, getSalesRepAnalysis, setCurrentViewState ]);

	// Set the selected Seller and change the view to Seller view
	const selectSeller = useAmplitudePassthrough(session, EventInsightsNames.OpenInsightsSeller, (Id: string, snapshotDate?: string) =>
	{
		dispatch(setSelectedSellerId(Id));
		dispatch(getSellerAnalysis({ sellerId: Id, snapshotDate }));
		setCurrentViewState({ view: InsightsViewState.Seller });
	}, [dispatch, setSelectedSellerId, getSellerAnalysis, setCurrentViewState ]);

	// Set the selected Grower and change the view to Grower view
	const selectGrower = useAmplitudePassthrough(session, EventInsightsNames.OpenInsightsForGrower, (Id: string, snapshotDate?: string) =>
	{
		dispatch(setSelectedGrowerId(Id));
		dispatch(getGrowerAnalysis({ growerId: Id, snapshotDate }));
		setCurrentViewState({ view: InsightsViewState.Grower });
	}, [dispatch, setSelectedGrowerId, getGrowerAnalysis, setCurrentViewState]);

	// Return to the previous view
	const backToLastView = () =>
	{
		switch (currentViewState.view)
		{
			case InsightsViewState.Territory :
				setCurrentViewState({ view: InsightsViewState.MultiTerritory });
				dispatch(clearSelectedTerritoryAnalysis());
				break;
			case InsightsViewState.SalesRep :
				// A sales rep reached from a territory goes back to that territory, not to the flat list
				if (currentTerritoryId)
				{
					setCurrentViewState({ view: InsightsViewState.Territory });
					dispatch(getTerritoryAnalysis({ territoryId: currentTerritoryId }));
				}
				else
				{
					setCurrentViewState({ view: InsightsViewState.MultiSalesRep });
				}
				dispatch(clearSelectedSalesRepAnalysis());
				break;
			case InsightsViewState.Seller :
				if(aiExperience === 'Seller')
				{
					setCurrentViewState({ view: InsightsViewState.SellerMembers });
				}
				else
				{
					setCurrentViewState({ view: InsightsViewState.SalesRep });
					if (salesRepId)
					{
						dispatch(getSalesRepAnalysis({ salesRepId }));
					}
				}
				dispatch(clearSelectedSellerAnalysis());
				break;
			case InsightsViewState.Grower :
				setCurrentViewState({ view: InsightsViewState.Seller });
				if (sellerId)
				{
					dispatch(getSellerAnalysis({ sellerId }));
				}
				dispatch(clearSelectedGrowerAnalysis());
				break;
		}
	};

	// Determine what view state to display
	const determineView = () =>
	{
		switch(currentViewState.view)
		{
			case InsightsViewState.Default :
				return (
					<div>
						{
							aiExperience === 'Unknown' || aiExperience === 'None' ?
								<div style={{ fontSize: theme.fontSizes.large, fontWeight: theme.fontWeights.bold }}>
									No Root Causes are available for your account.
								</div>
								:
								<div>Loading Root Cause Experience...</div>
						}
					</div>
				);
			case InsightsViewState.MultiTerritory :
				return (
					<MultiTerritoryView onSelectTerritory={selectTerritory} />
				);
			case InsightsViewState.Territory :
				return (
					<SingleTerritoryView
						onSelectSalesRep={selectSalesRep}
						backToLastView={backToLastView}
						changeSnapshotDate={selectTerritory}
						canGoBack={territoryAnalyses?.length > 1}
					/>
				);
			case InsightsViewState.MultiSalesRep :
				return (
					<MultiSalesRepView onSelectSalesRep={selectSalesRep} />
				);
			case InsightsViewState.SalesRep :
				return (
					<SingleSalesRepView onSelectSeller={selectSeller} backToLastView={backToLastView} changeSnapshotDate={selectSalesRep} />
				);
			case InsightsViewState.SellerMembers :
				return (
					<SellerMembersView onSelectSeller={selectSeller} />
				);
			case InsightsViewState.Seller :
				return (
					<SellerView onSelectGrower={selectGrower} backToLastView={backToLastView} changeSnapshotDate={selectSeller} />
				);
			case InsightsViewState.Grower :
				return (
					<GrowerView backToLastView={backToLastView} changeSnapshotDate={selectGrower} />
				);
		}
	};

	return (
		<MainContainer>
			<InnerContainer>
				<div style={{
					display: 'inline-flex', 
					alignItems: 'center',
					color:'#3d3d3d', 
					fontWeight: theme.fontWeights.bold, 
					fontSize: theme.fontSizes.xLarge,
					gap: 12,
					marginBottom: 8,
				}}>
					<RootCauseLogo 
						aria-label="Root Cause Report"
						style={{ 
							height: 44,
						}}
					/>
					<div
						style={{
							backgroundColor: 'currentColor',
							width: 2,
							height: 24,
						}}
					/>
					{currentLoggedInUserName}
				</div>
				{determineView()}
			</InnerContainer>
		</MainContainer>
	);
};

